import React, {useMemo, useState, useEffect} from 'react';
import {Button, Grid, Box, Typography, Tooltip} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router-dom';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import {useReviewContext} from '../../contexts/ReviewContext';
import useTitle from '../../hooks/useTitle';

import {ReportPackFragment} from '../../schema-types/ReportPackFragment';
import Loading from '../../components/Loading/Loading';

import {getReviewContacts} from './UploadDocumentReportPack';
import {useAccountContext, useAccountFull} from '../../contexts/AccountContext';
import {
  ReportPackStatus,
  ReportPackTemplateTypes,
  ReviewStatusOneviewEnum,
} from '../../schema-types/globalTypes';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';

import Footer from '../../components/Footer/Footer';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import {ColoredChip} from '../../components/EditorGrid/GridRenderComponents';

import {useReportPack} from './useReportPack';
import {getReportPackDisabled, ReportPackStatusColourScheme} from './ReportPackHelpers';
import {ReportPackContact} from './ReportPackContact';
import {ScrollableModal} from '../../components/Common/ScrollableModal';
import {ReportPackFile} from './ReportPackFile';
import {ReportPackGenerateSmartflow} from './ReportPackGenerateSmartflow';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {FormReadOnly} from '../../components/AutoForm/AutoFormNew';
import {NewReportPackModal} from './NewReportPackModal';
import {ConfirmationModal} from '../../components/Common/ConfirmationModal';
import {ErrorPage} from '../ErrorPage';
import {AccountBasicFragment_contacts} from '../../schema-types/AccountBasicFragment';

export const ReportPackPage: React.FC = () => {
  return (
    <>
      <HandoverStep />
      <ReportPackPageInner />
    </>
  );
};

export const ReportPackPageInner: React.FC = () => {
  const {accountFull} = useAccountFull();
  const {account} = useAccountContext();
  const {reviewDetails} = useReviewContext();
  const [isNewReportPackModalOpen, setIsNewReportPackModalOpen] = useState(false);
  useTitle(`Report Pack - ${reviewDetails?.account?.name}`);
  const {
    upsertData,
    upsertError,
    upsertLoading,
    dataReportPackQuery,
    loadingReportPackQuery,
    errorReportPackQuery,
  } = useReportPack();
  const report = dataReportPackQuery;
  const reviewContacts = useMemo(
    () => getReviewContacts(reviewDetails, account, report),
    [account, reviewDetails, report]
  );

  // Show modal when there's no report
  useEffect(() => {
    if (!loadingReportPackQuery && !report && accountFull && !isNewReportPackModalOpen) {
      setIsNewReportPackModalOpen(true);
    }
  }, [loadingReportPackQuery, report, accountFull, isNewReportPackModalOpen]);

  if (report) {
    return (
      <FormReadOnly readOnly={!report?.isEditable}>
        <ReportPack report={report} reviewContacts={reviewContacts} />
      </FormReadOnly>
    );
  }
  if (loadingReportPackQuery || upsertLoading || !accountFull) {
    return <Loading entityName="Report pack" />;
  }
  if (upsertError) {
    return <ErrorMessage error={upsertError} />;
  }
  if (upsertData?.upsertReportPack?.errors) {
    return (
      <ErrorMessage error={{message: upsertData?.upsertReportPack?.errors.join('.')}} />
    );
  }
  if (errorReportPackQuery) {
    return <ErrorMessage error={errorReportPackQuery} />;
  }

  return (
    <>
      <NewReportPackModal
        isOpen={isNewReportPackModalOpen}
        onClose={() => setIsNewReportPackModalOpen(false)}
      />
    </>
  );
};

const getReportPackErrors = (report: ReportPackFragment): string | false => {
  debugger;
  if (
    (report.templateType === ReportPackTemplateTypes.NO_ACTION_LETTER ||
      report.templateType === ReportPackTemplateTypes.DECLINED_LETTER ||
      report.templateType === ReportPackTemplateTypes.UNCONTACTABLE_LETTER) &&
    report.templateHtml?.includes('E70E0E')
  ) {
    return 'Report content includes red text.'; // Invalid report pack if it contains the E70E0E color code
  }
  return false;
};

const reportCanBeSubmitted = (report: ReportPackFragment): boolean => {
  if (report.status === ReportPackStatus.IN_PROGRESS) {
    return true;
  }
  if (report.status === ReportPackStatus.READY_TO_SUBMIT) {
    return true; // Can submit if the report pack is ready to submit
  }
  if (report.status === ReportPackStatus.SUBMITTED) {
    return false; // Cannot submit if the report pack has already been submitted
  }
  if (report.status === ReportPackStatus.PARTLY_SIGNED) {
    return false;
  } // Cannot submit if the report pack is partly signed
  if (report.status === ReportPackStatus.CANCELLED) {
    return false; // Cannot submit if the report pack is cancelled
  }
  if (report.status === ReportPackStatus.PARTLY_READ) {
    return false; // Cannot submit if the report pack is partly read
  }
  if (report.status === ReportPackStatus.READ) {
    return false; // Cannot submit if the report pack is read
  }
  return true;
};

export const ReportPack: React.FC<{
  report: ReportPackFragment;
  reviewContacts: AccountBasicFragment_contacts[];
}> = ({report, reviewContacts}) => {
  const {account} = useAccountContext();
  const {reviewDetails} = useReviewContext();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isMarkAsNolongerNeededModalOpen, setIsMarkAsNolongerNeededModalOpen] =
    useState(false);
  const [isResetConfirmationOpen, setIsResetConfirmationOpen] = useState(false);
  const {enqueueSnackbar} = useSnackbar();
  const contacts = useMemo(
    () => getReviewContacts(reviewDetails, account, report),
    [account, reviewDetails, report]
  );

  const {
    onSubmit,
    submitLoading,
    onCancelReportPack,
    cancelLoading,
    onDeleteReportPack,
    deleteLoading,
    onAmendReportPack,
    amendLoading,
    onMarkReportPackAsNoLongerValid,
  } = useReportPack();
  const navigate = useNavigate();
  const cancelOrAmendDisabled =
    (report.status !== ReportPackStatus.SUBMITTED &&
      report.status !== ReportPackStatus.PARTLY_SIGNED) ||
    amendLoading ||
    cancelLoading;

  const {djangoUser} = useDjangoContext();
  const canMarkAsNoLongerNeeded =
    djangoUser.isGod &&
    (report.status === ReportPackStatus.IN_PROGRESS ||
      report.status === ReportPackStatus.READY_TO_SUBMIT);
  const canCancelOrAmend =
    report.templateType === ReportPackTemplateTypes.ATTACHMENT_LETTER;

  const contactsWithoutEmail = account?.contacts?.filter((contact) => {
    const relevantContact = contact.accountContacts.find(
      (c) =>
        c.account.id === account.id && reviewContacts.find((rc) => rc.id === contact.id)
    );
    return !contact.email && (relevantContact?.isPrimary || relevantContact?.isSecondary);
  });

  const disabledTooltip = useMemo(() => {
    if (
      reviewDetails?.statusOneview !== ReviewStatusOneviewEnum.CLOSED &&
      reviewDetails?.statusOneview !== ReviewStatusOneviewEnum.COMPLETED
    ) {
      return 'Review must be closed or completed';
    }
    if (contactsWithoutEmail?.length) {
      return 'All contacts must have an email';
    }
    return null;
  }, [reviewDetails, contactsWithoutEmail]);

  const handleResetReportPack = () => {
    enqueueSnackbar(`Resetting report pack`, {variant: 'info'});
    onDeleteReportPack({
      variables: {
        reportPackId: report.id,
      },
    })
      .then(({data}) => {
        if (data?.deleteReportPack?.ok) {
          enqueueSnackbar(`Successfully reset report pack`, {
            variant: 'success',
          });
        } else {
          throw new Error(data?.deleteReportPack?.errors?.join('') || 'Unknown error');
        }
      })
      .catch((err) => {
        enqueueSnackbar(`Unable to reset report pack: ${err}`, {
          variant: 'error',
        });
      });
    setIsResetConfirmationOpen(false);
  };

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  return (
    <>
      {!!contactsWithoutEmail?.length && (
        <ErrorMessage
          error={{
            message: `The following contacts do not have a valid email:\n ${contactsWithoutEmail.map(
              (contact) => `${contact.simplifiedName}\n`
            )}`,
          }}
        />
      )}
      <Box
        position="relative"
        top={-10}
        pb={1}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <ColoredChip
          colorScheme={ReportPackStatusColourScheme}
          value={report.status}
          prefix="Report Pack status: "
        />
        {report.isEditable && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setIsResetConfirmationOpen(true)}
            disabled={deleteLoading}
          >
            Reset report pack
          </Button>
        )}
      </Box>
      <ReportPackFile reportPack={report} />
      <Grid container spacing={2} mb={8}>
        {report.templateType !== ReportPackTemplateTypes.ATTACHMENT_LETTER ? (
          <>
            <Grid item xs={6}>
              <Panel color={themeColors.purple} title="Generate documents:">
                <Box padding={1}>
                  <ReportPackGenerateSmartflow report={report} />
                </Box>
              </Panel>
            </Grid>
            <Grid item xs={6} />
          </>
        ) : null}
        {contacts?.map((contact) => {
          return (
            <Grid key={contact.id} item xs={6}>
              <Panel color={themeColors.purple} title={`${contact.name} forms:`}>
                <Box padding={1}>
                  <ReportPackContact report={report} contact={contact} />
                </Box>
              </Panel>
            </Grid>
          );
        })}
      </Grid>
      <Footer
        maxWidth={false}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '10px 26px 26px 26px',
          position: 'fixed',
          bottom: 0,
          left: 220,
          width: 'auto',
          right: 0,
          pointerEvents: 'none',
        }}
      >
        <Box display="flex" justifyContent="flex-end" flex={1}>
          <Button
            style={{pointerEvents: 'auto'}}
            sx={{mr: 2, backgroundColor: 'white'}}
            variant="outlined"
            color="primary"
            onClick={() => {
              navigate({
                pathname: `/account/${account?.id}/review-journey/${reviewDetails?.id}/summary`,
              });
            }}
          >
            Back
          </Button>
          {canMarkAsNoLongerNeeded && canCancelOrAmend && (
            <Button
              style={{pointerEvents: 'auto'}}
              sx={{mr: 2}}
              onClick={() => {
                setIsMarkAsNolongerNeededModalOpen(true);
              }}
              variant="contained"
              color="secondary"
            >
              Mark report pack as no longer needed
            </Button>
          )}
          {canCancelOrAmend && (
            <>
              <Button
                style={{pointerEvents: 'auto'}}
                sx={{mr: 2, backgroundColor: 'white'}}
                onClick={() => {
                  enqueueSnackbar(`Amending report pack`, {variant: 'info'});
                  onAmendReportPack({
                    variables: {
                      reportPackId: report.id,
                    },
                  })
                    .then(({data}) => {
                      if (data?.amendReportPack?.ok) {
                        enqueueSnackbar(`Successfully amended report pack`, {
                          variant: 'success',
                        });
                      } else {
                        throw new Error(
                          data?.amendReportPack?.errors?.join('') || 'Unknown error'
                        );
                      }
                    })
                    .catch((err) => {
                      enqueueSnackbar(`Unable to amend report pack: ${err}`, {
                        variant: 'error',
                      });
                    });
                }}
                variant="outlined"
                color="primary"
                disabled={cancelOrAmendDisabled || Boolean(contactsWithoutEmail?.length)}
              >
                Amend report pack
              </Button>
              <Button
                style={{pointerEvents: 'auto'}}
                sx={{mr: 2, backgroundColor: 'white'}}
                onClick={() => {
                  setIsCancelModalOpen(true);
                }}
                variant="outlined"
                color="secondary"
                disabled={cancelOrAmendDisabled || Boolean(contactsWithoutEmail?.length)}
              >
                Cancel report pack
              </Button>
            </>
          )}
        </Box>
        {reportCanBeSubmitted(report) && (
          <Tooltip title={disabledTooltip}>
            <Button
              style={{pointerEvents: 'auto'}}
              onClick={() => {
                if (getReportPackErrors(report)) {
                  enqueueSnackbar(`Cannot submit: ${getReportPackErrors(report)}`, {
                    variant: 'error',
                  });
                  return;
                }
                enqueueSnackbar(`Submitting report pack`, {
                  variant: 'info',
                  persist: true,
                });

                onSubmit({
                  variables: {
                    reportPackId: report.id,
                  },
                })
                  .then(({data}) => {
                    if (data?.submitReportPack?.ok) {
                      enqueueSnackbar(`Successfully submitted report pack`, {
                        variant: 'success',
                        persist: true,
                      });
                    } else {
                      throw new Error(
                        data?.submitReportPack?.errors?.join('') || 'Unknown error'
                      );
                    }
                  })
                  .catch((err) => {
                    enqueueSnackbar(`Unable to submit report pack: ${err}`, {
                      variant: 'error',
                    });
                  });
              }}
              variant="contained"
              color="primary"
              disabled={
                submitLoading ||
                getReportPackDisabled(report) ||
                Boolean(contactsWithoutEmail?.length) ||
                (reviewDetails.statusOneview !== ReviewStatusOneviewEnum.CLOSED &&
                  reviewDetails.statusOneview !== ReviewStatusOneviewEnum.COMPLETED)
              }
            >
              Submit report pack
            </Button>
          </Tooltip>
        )}
      </Footer>

      <ScrollableModal open={isCancelModalOpen} autoHeight>
        <Panel>
          <Typography>
            Are you sure you want to Cancel this report pack? By Selecting cancel you will
            mark this review as &apos;Cancelled - Not Proceeded&apos; with and you will
            not be able to re-issue a report
          </Typography>
          <Box display="flex" justifyContent="space-around" mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsCancelModalOpen(false)}
            >
              Do not cancel Report Pack
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setIsCancelModalOpen(false);
                enqueueSnackbar(`Cancelling report pack`, {variant: 'info'});
                onCancelReportPack({
                  variables: {
                    reportPackId: report.id,
                  },
                })
                  .then(({data}) => {
                    if (data?.cancelReportPack?.ok) {
                      enqueueSnackbar(`Successfully cancelled report pack`, {
                        variant: 'success',
                      });
                    } else {
                      throw new Error(
                        data?.cancelReportPack?.errors?.join('') || 'Unknown error'
                      );
                    }
                  })
                  .catch((err) => {
                    enqueueSnackbar(`Unable to cancel report pack: ${err}`, {
                      variant: 'error',
                    });
                  });
              }}
            >
              Cancel Report Pack
            </Button>
          </Box>
        </Panel>
      </ScrollableModal>
      <ScrollableModal open={isMarkAsNolongerNeededModalOpen} autoHeight>
        <Panel>
          <Typography>{`You should use the 'Mark Report pack as no longer needed' option if you have sent a report via DocuSign or by post and have updated Curo via the dialog, and will not need to use digital signing in the portal. This will remove the report pack from the Dashboard and will not update curo.`}</Typography>
          <Box display="flex" justifyContent="space-around" mt={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsMarkAsNolongerNeededModalOpen(false)}
            >
              Do not mark report pack as no longer needed
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={async () => {
                setIsMarkAsNolongerNeededModalOpen(false);
                enqueueSnackbar(`Marking report pack as no longer needed`, {
                  variant: 'info',
                });
                await onMarkReportPackAsNoLongerValid({
                  variables: {
                    reportPackId: report.id,
                  },
                })
                  .then(({data}) => {
                    if (data?.markReportPackAsNoLongerValid?.ok) {
                      enqueueSnackbar(`Marked report pack as no longer needed`, {
                        variant: 'success',
                      });
                    } else {
                      throw new Error(
                        data?.markReportPackAsNoLongerValid?.errors?.join('') ||
                          'Unknown error'
                      );
                    }
                  })
                  .catch((err) => {
                    enqueueSnackbar(
                      `Unable to mark report pack as no longer needed: ${err}`,
                      {
                        variant: 'error',
                      }
                    );
                  });
              }}
            >
              Mark report pack as no longer needed
            </Button>
          </Box>
        </Panel>
      </ScrollableModal>
      <ConfirmationModal
        open={isResetConfirmationOpen}
        handleClose={() => setIsResetConfirmationOpen(false)}
        title="Reset Report Pack"
        message="Are you sure you want to reset this report pack? All report pack forms and contents will be deleted."
        cancelText="Cancel"
        confirmText="Confirm"
        confirmAction={handleResetReportPack}
        loading={deleteLoading}
      />
    </>
  );
};
